import type { Patient, TreatmentPackage } from '@/types/database';

export type { Patient, TreatmentPackage };

export interface TreatmentPackageWithPatient extends TreatmentPackage {
  patient: Pick<Patient, 'id' | 'name' | 'phone' | 'email'>;
}

/** Item da lista de parcelas geradas para preview/criação. */
export interface InstallmentPreview {
  index: number; // 1-indexed
  label: string; // "Entrada" | "Parcela 1/12"
  date: Date;
  amount: number;
}

export interface InstallmentSummary {
  installments: InstallmentPreview[];
  totalCount: number;
  installmentAmount: number; // valor de cada parcela regular
  installmentTotal: number; // total parcelado (sem entrada)
  downPayment: number;
  total: number;
}

/**
 * Calcula a divisão de um pacote em parcelas mensais.
 * - Se houver entrada, ela é a primeira "parcela" com data start_date.
 * - Demais parcelas são divididas igualmente; o resíduo de centavos cai na última.
 */
export function calculateInstallments(
  totalAmount: number,
  downPayment: number,
  numberOfInstallments: number,
  startDate: Date,
): InstallmentSummary {
  const remaining = Math.max(0, totalAmount - downPayment);
  const installments: InstallmentPreview[] = [];
  let installmentAmount = 0;
  let installmentTotal = 0;

  if (downPayment > 0) {
    installments.push({
      index: 1,
      label: 'Entrada',
      date: new Date(startDate),
      amount: round2(downPayment),
    });
  }

  if (numberOfInstallments > 0 && remaining > 0) {
    const baseValue = Math.floor((remaining * 100) / numberOfInstallments) / 100;
    installmentAmount = baseValue;
    let accumulated = 0;
    for (let i = 0; i < numberOfInstallments; i++) {
      const isLast = i === numberOfInstallments - 1;
      const value = isLast ? round2(remaining - accumulated) : baseValue;
      accumulated = round2(accumulated + value);
      const date = new Date(startDate);
      // Se há entrada, primeira parcela é 1 mês após start_date
      const monthOffset = downPayment > 0 ? i + 1 : i;
      date.setMonth(date.getMonth() + monthOffset);
      installments.push({
        index: installments.length + 1,
        label: `Parcela ${i + 1}/${numberOfInstallments}`,
        date,
        amount: value,
      });
    }
    installmentTotal = round2(accumulated);
  }

  return {
    installments,
    totalCount: installments.length,
    installmentAmount,
    installmentTotal,
    downPayment,
    total: round2(downPayment + installmentTotal),
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
